import { LessonAgeGroupsEnum, SkillLevelValuesEnum, SportsEnum } from 'types/generated/client';
import { CertificationEnum } from './props';

export interface Certification {
  title: string;
  value: CertificationEnum;
  imageSrc: string;
}

export const MIN_PRICE = 0;
export const MAX_PRICE = 500;

/**
 * "All / Instant book" segmented control. It only exists in the mobile filters modal
 * — on desktop the list already splits instant-book coaches into their own "Top
 * coaches" section (see `LessonList`), so there is nothing to choose between.
 */
export enum CoachAvailabilityFilter {
  All = 'all',
  InstantBook = 'instant',
}

export const PPR_CERTIFICATION = {
  title: 'PPR Certified',
  value: CertificationEnum.PPR_CERTIFIED,
  imageSrc: '/images/coaches/ppr-vertical.png',
};

export const PTR_CERTIFICATION = {
  title: 'PTR Certified',
  value: CertificationEnum.PTR_CERTIFIED,
  imageSrc: '/images/coaches/ptr-vertical.png',
};
export const PCR_CERTIFICATION = {
  title: 'PCR Certified',
  value: CertificationEnum.PCR_CERTIFIED,
  imageSrc: '/images/coaches/pcr-vertical.png',
};

// export const PCI_CERTIFICATION = {
//   title: 'PCI Certified',
//   value: CertificationEnum.PCI_CERTIFIED,
//   imageSrc: '/images/coaches/pci-badge.png',
// };

export const VAIR_PRO_CERTIFICATION = {
  title: 'VAIR PRO',
  value: CertificationEnum.VAIR_PRO,
  imageSrc: '/images/coaches/vair-pro.png',
};

// export const CARDIO_TENNIS_CERTIFICATION = {
//   title: 'Cardio Tennis',
//   value: CertificationEnum.CARDIO_TENNIS,
//   imageSrc: '/images/coaches/cardio-tennis-badge.png',
// };

/**
 * Certifications offered per sport in the marketplace filters, per the Figma
 * `Filters_Clinics_Pickleball` and `Filters_Clinics_Tennis` variants. Padel keeps its
 * single governing body until design covers it.
 */
export const CERTIFICATIONS_BY_SPORT: Partial<Record<SportsEnum, Certification[]>> = {
  [SportsEnum.Pickleball]: [PPR_CERTIFICATION, VAIR_PRO_CERTIFICATION],
  [SportsEnum.Tennis]: [PTR_CERTIFICATION],
  [SportsEnum.Padel]: [PCR_CERTIFICATION],
};

/**
 * "Works with" spans two different coach relationships — age groups
 * (`coachLessonAgeGroups.lessonAgeGroup`) and skill levels
 * (`coachLessonSkillLevels.skillLevel`) — so each option carries the source it
 * matches against. `id` is what the filter state stores; it is namespaced by kind so
 * the two value spaces can never collide.
 *
 * Note the design's labels do not match the raw enums: "Kids" is YOUTH and "Experts"
 * is ADVANCED (the highest level a coach can actually select in "Who I teach").
 */
export type WorksWithKind = 'ageGroup' | 'skillLevel';

export interface WorksWithOption {
  id: string;
  label: string;
  kind: WorksWithKind;
  value: string;
}

const worksWithOption = (kind: WorksWithKind, value: string, label: string): WorksWithOption => ({
  id: `${kind}:${value}`,
  label,
  kind,
  value,
});

export const WORKS_WITH_OPTIONS: WorksWithOption[] = [
  worksWithOption('ageGroup', LessonAgeGroupsEnum.Adult, 'Adults'),
  worksWithOption('ageGroup', LessonAgeGroupsEnum.Youth, 'Kids'),
  worksWithOption('ageGroup', LessonAgeGroupsEnum.Senior, 'Seniors'),
  worksWithOption('skillLevel', SkillLevelValuesEnum.Beginner, 'Beginners'),
  worksWithOption('skillLevel', SkillLevelValuesEnum.Intermediate, 'Intermediates'),
  worksWithOption('skillLevel', SkillLevelValuesEnum.Advanced, 'Experts'),
];
